import {
  TypeImplementation,
  withSystem,
  withMeta,
  EnvTypes,
} from '@packages/systems/wfdl';
import {functionOf, TText, TListOfElement} from '@packages/systems/wfdl-ast';
import {qualify, iconName} from '@packages/systems/wfdl-Qualified';
import {pluginName} from '../constants';
import {
  dynamoTextualContentToListOfElements,
  dynamoTextualContentToText,
} from '../transformers';
import {TOption} from '@packages/systems/plugins/Option';
import {TBasicLink} from '../Link';
import type {DynamoEnvValueType} from '../types';
import {createDynamoTextOperators} from '../utils';
import {TDynamoEmail} from '@packages/systems/plugins-utils/DynamoGateway';

export const EmailType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(qualify(pluginName, iconName('Email'))),
});

export const dynamoEmailToText = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoEmail)),
      TText
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: dynamoTextualContentToText,
} as const;

export const dynamoEmailToListOfElements = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoEmail)),
      TListOfElement
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: dynamoTextualContentToListOfElements,
} as const;

type EmailLinkLiteral = {
  email: string;
  mode: 'email';
  subject?: string;
};

/**
 * @deprecated - replaced by the V2 implicit below
 *
 * Should be exported as hidden to ensure backwards compatibility before
 * we have run the site data migration to remove calls to this function
 * from element data.
 *
 * Should be deleted after the migration has been shipped.
 */
export const dynamoEmailToBasicLink = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoEmail)),
      TBasicLink
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: (option: DynamoEnvValueType<string>): EmailLinkLiteral => {
    if (option === null || option.tag === 'None') {
      return {
        email: '',
        mode: 'email',
      };
    }

    return {
      email: option.val,
      mode: 'email',
      subject: '',
    };
  },
} as const;

export const dynamoEmailToBasicLinkV2: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'Email link settings'},
      functionOf(
        withMeta(
          {placeholder: 'e.g. You’ve got mail!'},
          withSystem({label: 'Subject'}, TText)
        ),
        functionOf(
          withSystem({isBindable: true}, TOption(TDynamoEmail)),
          TBasicLink
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (subject: string) => {
    return (option: DynamoEnvValueType<string>): EmailLinkLiteral => {
      if (option === null || option.tag === 'None' || !option.val) {
        return {
          email: '',
          mode: 'email',
        };
      }

      return {
        email: option.val,
        mode: 'email',
        subject,
      };
    };
  },
};

// Generate all text-based operators using the generic utility function
const emailOperators = createDynamoTextOperators(TDynamoEmail);

export const dynamoEmailEquals = emailOperators.equals;
export const dynamoEmailDoesNotEqual = emailOperators.doesNotEqual;
export const dynamoEmailContains = emailOperators.contains;
export const dynamoEmailDoesNotContain = emailOperators.doesNotContain;
export const dynamoEmailStartsWith = emailOperators.startsWith;
export const dynamoEmailDoesNotStartWith = emailOperators.doesNotStartWith;
export const dynamoEmailEndsWith = emailOperators.endsWith;
export const dynamoEmailDoesNotEndWith = emailOperators.doesNotEndWith;
export const dynamoEmailIsSet = emailOperators.isSet;
export const dynamoEmailIsNotSet = emailOperators.isNotSet;
export const dynamoEmailOperand = emailOperators.operand;
